import { Nav } from "../components/Nav";
import { Logo } from "../components/Logo";
import { Link } from "react-router-dom";
import { PrintPdfButton } from "../components/PrintPdfButton";
import { VersionHistory } from "../components/VersionHistory";
import { LegalLanguageSwitcher } from "../components/LegalLanguageSwitcher";
import { Mail, ArrowLeft, FileSignature, Download, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { api, API_URL } from "../lib/api";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const SECTIONS = [
    {
        n: 1,
        title: "Definitions",
        body: 'For the purposes of this DPA, capitalized terms have the meanings set out below or in the applicable data protection laws. "Customer" means the SocialPulse partner, business or organization entering into this DPA. "Processor" means SpLink.pro™. "Personal Data", "Controller", "Processor", "Data Subject", "Processing", and "Sub-processor" have the meanings given in the General Data Protection Regulation (Regulation (EU) 2016/679) and other applicable data protection laws.',
    },
    {
        n: 2,
        title: "Scope & Roles",
        body: "This DPA applies to the Processing of Personal Data by SpLink.pro on behalf of the Customer in connection with the SpLink.pro™ URL shortening, QR code and analytics services. The Customer acts as the Controller of Personal Data and SpLink.pro acts as the Processor.",
    },
    {
        n: 3,
        title: "Subject Matter, Duration, Nature & Purpose",
        body: "The subject matter of Processing is the provision of URL shortening, QR code generation, click and scan tracking, and related analytics for the Customer. The duration of Processing is for the term of the Customer's use of the services, and thereafter for the retention period described in Section 10. The nature and purpose of Processing is to enable the Customer to shorten URLs, generate QR codes and receive analytics related to their audience.",
    },
    {
        n: 4,
        title: "Categories of Data Subjects & Personal Data",
        body: "Data Subjects may include: end-users of the Customer's own websites and communications who click Customer's short links or scan Customer's QR codes; the Customer's own personnel who administer the account.",
        after: "Categories of Personal Data may include:",
        afterBullets: [
            "Account data: name, email address, business/organization details supplied by Customer.",
            "Technical event data: IP address, browser and device information, operating system, referrer, coarse geographic location, timestamps of link clicks and QR scans.",
            "URL content: long URLs and any Personal Data the Customer chooses to encode in short links or QR codes.",
        ],
    },
    {
        n: 5,
        title: "Obligations of the Processor",
        body: "SpLink.pro shall:",
        bullets: [
            "Process Personal Data only on documented instructions from the Customer, including with regard to transfers to third countries.",
            "Ensure that persons authorized to Process Personal Data are subject to appropriate confidentiality obligations.",
            "Implement appropriate technical and organizational measures to ensure a level of security appropriate to the risk (see Section 8).",
            "Assist the Customer, taking into account the nature of Processing, in fulfilling requests from Data Subjects.",
            "Make available to the Customer all information reasonably necessary to demonstrate compliance with this DPA.",
        ],
    },
    {
        n: 6,
        title: "Sub-processors",
        body: "The Customer provides a general authorization for SpLink.pro to engage Sub-processors to Process Personal Data, provided that SpLink.pro imposes on each Sub-processor data protection obligations no less protective than those in this DPA. SpLink.pro maintains an up-to-date list of Sub-processors and will provide the Customer with reasonable prior notice of changes.",
    },
    {
        n: 7,
        title: "Data Subject Rights",
        body: "Taking into account the nature of the Processing, SpLink.pro shall assist the Customer by appropriate technical and organizational measures, insofar as this is possible, in responding to requests from Data Subjects exercising their rights under applicable data protection laws (including access, rectification, erasure, restriction, portability, and objection).",
    },
    {
        n: 8,
        title: "Security Measures",
        body: "SpLink.pro maintains a written information security program that includes, at minimum:",
        bullets: [
            "Encryption of Personal Data in transit over public networks (TLS).",
            "Access controls limiting production access to authorized personnel only.",
            "Hashing of user account passwords using an industry-standard adaptive algorithm.",
            "Regular backups and disaster-recovery procedures.",
            "Vulnerability management and periodic patching of underlying infrastructure.",
            "Logging of administrative actions on the platform.",
        ],
    },
    {
        n: 9,
        title: "Breach Notification",
        body: "SpLink.pro shall notify the Customer without undue delay after becoming aware of a Personal Data Breach affecting Customer Personal Data, and shall provide the Customer with the information reasonably required to comply with its notification obligations under applicable data protection laws.",
    },
    {
        n: 10,
        title: "Data Transfers",
        body: "Where SpLink.pro transfers Personal Data outside the European Economic Area or the United Kingdom, such transfers will be subject to an appropriate transfer mechanism (such as the EU Standard Contractual Clauses and, where applicable, the UK International Data Transfer Addendum), which are incorporated into this DPA by reference where required by applicable law.",
    },
    {
        n: 11,
        title: "Return & Deletion",
        body: "Upon termination of the services, SpLink.pro will, at the Customer's choice, delete or return all Personal Data Processed on behalf of the Customer, and delete existing copies, unless applicable law requires further storage. Analytics event data older than the standard retention period may be aggregated or deleted in the ordinary course of operations.",
    },
    {
        n: 12,
        title: "Audit Rights",
        body: "SpLink.pro shall make available to the Customer information reasonably necessary to demonstrate compliance with this DPA. On written request and no more than once per year, the Customer may audit SpLink.pro's compliance under this DPA, subject to reasonable confidentiality and security obligations, or accept third-party audit reports where available.",
    },
    {
        n: 13,
        title: "Governing Law",
        body: "This DPA is governed by the same law as the underlying Terms of Service between the Customer and SpLink.pro, unless applicable data protection law requires otherwise.",
    },
];

export default function DPA() {
    const { user } = useAuth();
    const [signatures, setSignatures] = useState([]);
    const [signOpen, setSignOpen] = useState(false);
    const [signBusy, setSignBusy] = useState(false);
    const [fCompany, setFCompany] = useState("");
    const [fName, setFName] = useState("");
    const [fTitle, setFTitle] = useState("");
    const [fEmail, setFEmail] = useState("");

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const { data } = await api.get("/dpa/my-signatures");
                setSignatures(data.items || []);
            } catch {}
        })();
    }, [user]);

    useEffect(() => {
        if (user) {
            setFName((n) => n || user.name || "");
            setFEmail((e) => e || user.email || "");
        }
    }, [user]);

    const submitSign = async (e) => {
        e.preventDefault();
        setSignBusy(true);
        try {
            const { data } = await api.post("/dpa/sign", {
                company_name: fCompany.trim(),
                name: fName.trim(),
                title: fTitle.trim() || undefined,
                email: fEmail.trim() || undefined,
            });
            setSignatures((s) => [data, ...s]);
            setSignOpen(false);
            toast.success("DPA e-signed. Downloading executed copy…");
            // Trigger download
            window.location.href = `${API_URL}/dpa/signature/${data.signature_id}.txt`;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to sign");
        } finally { setSignBusy(false); }
    };

    return (
        <div className="App min-h-screen">
            <Nav />
            <LegalLanguageSwitcher doc="dpa">

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-border">
                <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" aria-hidden />
                <div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-[hsl(217,91%,60%)]/10 blur-3xl" aria-hidden />
                <div className="relative mx-auto max-w-4xl px-6 pb-14 pt-24">
                    <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="dpa-back-link">
                        <ArrowLeft className="h-4 w-4" /> Back to home
                    </Link>
                    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium uppercase tracking-widest text-muted-foreground" data-testid="dpa-badge">
                        <FileSignature className="h-3 w-3 text-[hsl(217,91%,45%)]" />
                        Legal · Business & Enterprise
                    </div>
                    <h1 className="mt-6 font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl" data-testid="dpa-heading">
                        SpLink.pro™<br />
                        <span className="brand-gradient-text">Data Processing Addendum</span>
                    </h1>
                    <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
                        <div className="font-mono text-xs text-muted-foreground">
                            <span className="text-foreground/60">Effective date:</span>{" "}
                            <span className="ml-1">August 4, 2026</span>
                        </div>
                        <div className="font-mono text-xs text-muted-foreground">
                            <span className="text-foreground/60">Version:</span>{" "}
                            <span className="ml-1">v1.0</span>
                        </div>
                        <PrintPdfButton label="Download & sign as PDF" testId="dpa-print-btn" />
                        {user ? (
                            <Button
                                type="button"
                                size="sm"
                                className="no-print gap-2 bg-primary hover:bg-primary/90"
                                onClick={() => setSignOpen(true)}
                                data-testid="dpa-esign-btn"
                            >
                                <FileSignature className="h-4 w-4" /> E-sign DPA
                            </Button>
                        ) : (
                            <Button asChild size="sm" variant="outline" className="no-print gap-2" data-testid="dpa-signin-esign-btn">
                                <Link to="/login">
                                    <FileSignature className="h-4 w-4" /> Sign in to e-sign
                                </Link>
                            </Button>
                        )}
                    </div>

                    {signatures.length > 0 && (
                        <div className="mt-6 border-2 border-[hsl(100,76%,42%)]/40 bg-[hsl(100,76%,42%)]/5 p-5" data-testid="dpa-signed-banner">
                            <div className="flex items-center gap-2 font-display text-base font-semibold">
                                <Check className="h-4 w-4 text-[hsl(100,76%,35%)]" />
                                You e-signed this DPA on {new Date(signatures[0].signed_at).toLocaleDateString()}
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                                Company: <span className="font-mono">{signatures[0].company_name}</span> · Signature ID: <span className="font-mono">{signatures[0].signature_id}</span> · Version: {signatures[0].dpa_version}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <Button asChild size="sm" variant="outline" data-testid="dpa-download-signed-btn">
                                    <a href={`${API_URL}/dpa/signature/${signatures[0].signature_id}.txt`} download>
                                        <Download className="mr-2 h-4 w-4" /> Download signed copy
                                    </a>
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setSignOpen(true)} data-testid="dpa-resign-btn">
                                    Re-sign with new details
                                </Button>
                            </div>
                        </div>
                    )}
                    <p className="mt-8 max-w-3xl text-base leading-relaxed text-muted-foreground">
                        This Data Processing Addendum (the "<strong className="text-foreground">DPA</strong>") supplements the Terms of Service between the Customer (a SocialPulse partner, business or organization) and <strong className="text-foreground">SpLink.pro™</strong> and governs the Processing of Personal Data by SpLink.pro on behalf of the Customer.
                    </p>
                    <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
                        Business and enterprise partners: download this DPA, print or "Save as PDF" from the print dialog, complete the signature block on the final page, and email a scanned copy to <a href="mailto:support@splink.pro" className="text-primary hover:underline">support@splink.pro</a>. We will countersign and return an executed copy for your compliance folder.
                    </p>
                </div>
            </section>

            {/* Body */}
            <section className="border-b border-border">
                <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 py-16 lg:grid-cols-[240px_1fr]">
                    {/* TOC */}
                    <aside className="hidden lg:block">
                        <div className="sticky top-24">
                            <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">Contents</div>
                            <nav className="flex flex-col gap-1.5 font-mono text-xs">
                                {SECTIONS.map((s) => (
                                    <a
                                        key={s.n}
                                        href={`#section-${s.n}`}
                                        className="text-muted-foreground hover:text-foreground"
                                        data-testid={`toc-link-${s.n}`}
                                    >
                                        <span className="text-[hsl(217,91%,45%)]">{String(s.n).padStart(2, "0")}</span>{" "}
                                        {s.title}
                                    </a>
                                ))}
                                <a href="#signatures" className="text-muted-foreground hover:text-foreground" data-testid="toc-link-signatures">
                                    <span className="text-[hsl(100,76%,42%)]">14</span> Signatures
                                </a>
                            </nav>
                        </div>
                    </aside>

                    <article className="max-w-3xl">
                        {SECTIONS.map((s) => (
                            <section
                                key={s.n}
                                id={`section-${s.n}`}
                                className="border-b border-border py-8 first:pt-0"
                                data-testid={`dpa-section-${s.n}`}
                            >
                                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                                    Section {String(s.n).padStart(2, "0")}
                                </div>
                                <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                                    {s.title}
                                </h2>
                                <p className="mt-4 text-base leading-relaxed text-muted-foreground">{s.body}</p>
                                {s.bullets && (
                                    <ul className="mt-4 space-y-2">
                                        {s.bullets.map((b) => (
                                            <li key={b} className="flex gap-3 text-base text-muted-foreground">
                                                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(217,91%,45%)]" />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {s.after && (
                                    <p className="mt-4 text-base leading-relaxed text-muted-foreground">{s.after}</p>
                                )}
                                {s.afterBullets && (
                                    <ul className="mt-4 space-y-2">
                                        {s.afterBullets.map((b) => (
                                            <li key={b} className="flex gap-3 text-base text-muted-foreground">
                                                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(100,76%,42%)]" />
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>
                        ))}

                        {/* Signature block */}
                        <section id="signatures" className="py-8" data-testid="dpa-signatures">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">Section 14</div>
                            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                                Signatures
                            </h2>
                            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                                By signing below, each party agrees to be bound by the terms of this DPA.
                            </p>

                            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
                                <SignatureBlock title="For the Customer (Controller)" testId="signature-customer" />
                                <SignatureBlock title="For SpLink.pro™ (Processor)" testId="signature-processor" prefill={{
                                    company: "SpLink.pro™",
                                    name: "____________________________",
                                    title: "Authorized signatory",
                                }} />
                            </div>

                            <div className="mt-10 border-t border-border pt-8">
                                <div className="flex flex-wrap items-center justify-between gap-4">
                                    <div className="text-sm text-muted-foreground">
                                        Return a scanned copy to{" "}
                                        <a href="mailto:support@splink.pro" className="text-primary hover:underline">support@splink.pro</a>.
                                    </div>
                                    <Button asChild variant="outline" className="no-print gap-2" data-testid="dpa-email-btn">
                                        <a href="mailto:support@splink.pro?subject=Signed%20DPA%20-%20SpLink.pro">
                                            <Mail className="h-4 w-4" /> Email signed DPA
                                        </a>
                                    </Button>
                                </div>
                            </div>
                        </section>

                        <VersionHistory docKey="dpa" testId="dpa-version-history" />
                    </article>
                </div>
            </section>

            </LegalLanguageSwitcher>

            {/* E-Sign Dialog */}
            <Dialog open={signOpen} onOpenChange={setSignOpen}>
                <DialogContent data-testid="dpa-esign-dialog">
                    <DialogHeader>
                        <DialogTitle>E-sign the SpLink.pro DPA</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        By submitting this form, you electronically execute the current version
                        ({/*hard-coded because backend always tags the latest*/ "v1.0"}) of the SpLink.pro™ Data Processing Addendum on
                        behalf of your organization. We'll store a timestamped, IP-stamped record
                        and email the executed copy to the contact address below.
                    </p>
                    <form onSubmit={submitSign} className="mt-4 space-y-3">
                        <div>
                            <Label htmlFor="sig-company">Company / Organization *</Label>
                            <Input id="sig-company" required value={fCompany} onChange={(e) => setFCompany(e.target.value)} className="mt-2" data-testid="sig-company-input" />
                        </div>
                        <div>
                            <Label htmlFor="sig-name">Your full name *</Label>
                            <Input id="sig-name" required value={fName} onChange={(e) => setFName(e.target.value)} className="mt-2" data-testid="sig-name-input" />
                        </div>
                        <div>
                            <Label htmlFor="sig-title">Title</Label>
                            <Input id="sig-title" value={fTitle} onChange={(e) => setFTitle(e.target.value)} placeholder="e.g. DPO, CTO, Founder" className="mt-2" data-testid="sig-title-input" />
                        </div>
                        <div>
                            <Label htmlFor="sig-email">Contact email</Label>
                            <Input id="sig-email" type="email" value={fEmail} onChange={(e) => setFEmail(e.target.value)} className="mt-2" data-testid="sig-email-input" />
                        </div>
                        <Button type="submit" disabled={signBusy || !fCompany.trim() || !fName.trim()} className="w-full bg-primary hover:bg-primary/90" data-testid="sig-submit-btn">
                            {signBusy ? "Signing..." : "E-sign & download executed copy"}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>

            <footer className="mx-auto max-w-6xl px-6 py-12">
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                    <Logo />
                    <div className="flex flex-wrap items-center gap-6 font-mono text-xs text-muted-foreground">
                        <Link to="/about" className="hover:text-foreground">About</Link>
                        <Link to="/terms" className="hover:text-foreground">Terms</Link>
                        <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
                        <Link to="/cookies" className="hover:text-foreground">Cookies</Link>
                        <Link to="/dpa" className="hover:text-foreground">DPA</Link>
                        <span>© {new Date().getFullYear()} SpLink.pro™</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function SignatureBlock({ title, prefill = {}, testId }) {
    return (
        <div className="border border-border bg-card p-6" data-testid={testId}>
            <div className="font-display text-lg font-semibold">{title}</div>
            <div className="mt-6 space-y-5">
                <SigLine label="Company / Organization" value={prefill.company} />
                <SigLine label="Full name" value={prefill.name} />
                <SigLine label="Title" value={prefill.title} />
                <SigLine label="Email" value={prefill.email} />
                <SigLine label="Signature" value={prefill.signature} tall />
                <SigLine label="Date" value={prefill.date} />
            </div>
        </div>
    );
}

function SigLine({ label, value, tall }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className={`mt-1 border-b border-foreground/40 ${tall ? "h-12" : "h-7"} font-mono text-sm`}>
                {value || "\u00A0"}
            </div>
        </div>
    );
}
